// Turns the flat object list into the shape an operator actually navigates:
// station → product → objects.
//
// ── Why three levels, and why the middle one collapses ──────────────────────
//
// A locker station with ten compartments used to render ten rows with the same
// location and the same service. At a hundred stations the page is unusable, so
// the station is the row and its contents fold away underneath.
//
// The product level is not an extra tier bolted between them — it is the level
// the object rows already stood on, made explicit. A station's contents are its
// *products*: ten interchangeable lockers are one product with ten units, not
// ten things. That matters beyond tidiness, because the object rows are not
// trustworthy for a pool: `getAssetAvailabilityForMonth` hangs every order of a
// group onto every asset of that group, so ten compartments each show all ten
// bookings rather than their own. Rolling those ten rows up would count each
// booking ten times. Rolling them up *by product* counts it once, because the
// bookings are a property of the product to begin with.
//
// So the level pays for itself only where it says something new, and it
// collapses everywhere else:
//
//   * a product with ONE object renders as one row, with no nesting — which is
//     the common case at a mixed rental station (one window wiper, one game
//     set) and at every room. No empty middle tier, no double row.
//   * a product with SEVERAL objects renders as one row that expands to its
//     objects — the locker bank, the two drills.
//
// A venue or apartment therefore falls out as station = product = object, one
// row, exactly as today (architecture §2d, "room-as-station"). It is the
// degenerate case of the same rule rather than a branch beside it.
//
// The industry splits it the same way: a hotel tape chart is room *type* over
// rooms, self-storage software is unit *type* over units. The bookable thing
// and the physical thing are different, and the bookable one is the one whose
// numbers mean anything.

import { AssetAvailability } from '../../../api/types';

export interface ProductRow {
    productId: string;
    productName: string;
    /** Objects fulfilling this product at this station. Never empty. */
    objects: AssetAvailability[];
    /**
     * Whether this row has anything to expand to. False for a single-unit
     * product, where the product row *is* the object row.
     */
    expandable: boolean;
}

export interface StationRow {
    stationId: string;
    stationName: string;
    locationId: string;
    locationName: string;
    /**
     * Other stations standing at the same address.
     *
     * Keyed on `locationId`, never on `locationName`: two locations can carry
     * the same name, and matching on the name is the very fragility this exists
     * to expose — one room becomes two locations the moment somebody
     * abbreviates "Straße" (PO, 2026-09-10). The id is already on every asset
     * row, so this costs no request.
     */
    siblingStationNames: string[];
    products: ProductRow[];
    /** Every object at this station, across its products. */
    objectCount: number;
}

/**
 * A station of one for an object that has none.
 *
 * `asset.depot_id` is nullable, so this covers the shape the schema permits —
 * not an expectation about the data. Every object carries a station today,
 * apartments and venues included, precisely so the business logic stays single
 * (lead dev, 2026-08-31); this branch should therefore never run. It exists
 * because dropping a station-less object would silently shrink the list, which
 * is the one outcome the grouping must never produce.
 *
 * It is not a special case either way: an object without a station *is* a
 * station holding one object, which is what the model already says about a
 * room. Keyed on the asset id so two such objects never merge into one row.
 */
const ownStationKey = (asset: AssetAvailability): string => `asset:${asset.assetId}`;

/** A product of one for an object whose product is unknown, for the same reason. */
const ownProductKey = (asset: AssetAvailability): string => `asset:${asset.assetId}`;

/**
 * Group objects into stations and, within each, into products.
 *
 * Input order is preserved: the caller has already sorted the objects, and a
 * station takes the position of its first object so the sort still reads.
 */
export const groupByStation = (assets: AssetAvailability[]): StationRow[] => {
    const stations = new Map<string, StationRow>();
    const productsByStation = new Map<string, Map<string, ProductRow>>();

    assets.forEach((asset) => {
        const stationKey = asset.stationId ?? ownStationKey(asset);
        const productKey = asset.productId ?? ownProductKey(asset);

        let station = stations.get(stationKey);
        if (!station) {
            station = {
                stationId: stationKey,
                // An object standing in as its own station is named after
                // itself — "Apartment 3", not a station label it never had.
                stationName: asset.stationName ?? asset.assetName,
                locationId: asset.locationId,
                locationName: asset.locationName,
                siblingStationNames: [],
                products: [],
                objectCount: 0,
            };
            stations.set(stationKey, station);
            productsByStation.set(stationKey, new Map());
        }

        const products = productsByStation.get(stationKey) as Map<string, ProductRow>;
        let product = products.get(productKey);
        if (!product) {
            product = {
                productId: productKey,
                productName: asset.productName ?? asset.assetName,
                objects: [],
                expandable: false,
            };
            products.set(productKey, product);
            station.products.push(product);
        }

        product.objects.push(asset);
        station.objectCount += 1;
    });

    // Expandability is decided once, at the end: a product is only worth
    // expanding when it holds more than the row already shows.
    stations.forEach((station) => {
        station.products.forEach((product) => {
            product.expandable = product.objects.length > 1;
        });
    });

    // Who else stands at each address — computed after every station is known,
    // because the first station at a location cannot know about the second.
    const stationNamesByLocation = new Map<string, string[]>();
    stations.forEach((station) => {
        if (!station.locationId) return;
        const names = stationNamesByLocation.get(station.locationId) ?? [];
        names.push(station.stationName);
        stationNamesByLocation.set(station.locationId, names);
    });
    stations.forEach((station) => {
        const names = stationNamesByLocation.get(station.locationId) ?? [];
        station.siblingStationNames = names.filter((name) => name !== station.stationName);
    });

    // `Array.from` rather than spreading the iterator: the admin-ui
    // tsconfig targets below es2015, where spreading a Map iterator does not
    // compile.
    return Array.from(stations.values());
};

/**
 * Does this station have any booking on this day?
 *
 * The **only** statement the current source can carry at station level. A count
 * cannot be aggregated from it — the group-to-asset fan-out described above
 * would count one booking once per object — but *existence* is immune to
 * duplication: ten copies of one booking still answer "yes, something is
 * booked", which is exactly why the station row asks this and not "how many".
 * No de-duplication is needed for the same reason; it would be ceremony that
 * changes no answer.
 *
 * One known imprecision, in the safe direction: if a product's objects are
 * spread across two stations, its bookings appear at both. The day then reads
 * "something is booked" where there may be nothing — never "free" where
 * something is booked.
 */
export const stationHasBookingOn = (
    station: StationRow,
    dayStart: Date,
    dayEnd: Date,
    occupies: (order: AssetAvailability['orders'][number]) => boolean,
): boolean => station.products.some((product) => product.objects.some(
    (asset) => asset.orders.some((order) => occupies(order)
        && new Date(order.startDate) < dayEnd
        && new Date(order.endDate) > dayStart),
));
